import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import type { OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import type { ComponentRef } from '@angular/core';
import { inject, Injectable, Injector } from '@angular/core';

import { take } from 'rxjs/operators';

import type {
  EvaluationPanelCandidate,
  EvaluationPanelData,
  EvaluationPanelMentionUsers,
  EvaluationPanelOffer,
  EvaluationPanelOpenOptions,
} from '@recruitee/evaluations-types';

import { EvaluationPanelContainerComponent } from './evaluation-panel-container.component';

@Injectable({ providedIn: 'root' })
export class EvaluationPanelService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);

  private overlayRef: OverlayRef | null = null;
  private componentRef: ComponentRef<EvaluationPanelContainerComponent> | null = null;

  public get isOpen(): boolean {
    return !!this.overlayRef;
  }

  public open(
    data: EvaluationPanelData,
    boundary: HTMLElement,
    options?: EvaluationPanelOpenOptions,
  ): void {
    if (this.overlayRef) {
      this.componentRef?.instance
        .confirmReplace()
        .pipe(take(1))
        .subscribe(confirmed => {
          if (!confirmed) return;

          this.componentRef?.setInput('data', data);
          this.componentRef?.setInput('boundary', boundary);
        });

      return;
    }

    this.overlayRef = this.overlay.create(
      new OverlayConfig({
        hasBackdrop: true,
        positionStrategy: this.overlay.position().global().centerHorizontally().bottom('0'),
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      }),
    );

    this.componentRef = this.overlayRef.attach(
      new ComponentPortal(EvaluationPanelContainerComponent, null, this.injector),
    );

    const backdrop = this.overlayRef.backdropElement;
    if (backdrop) {
      backdrop.style.backgroundColor = 'transparent';
      backdrop.style.pointerEvents = 'none';
    }

    this.overlayRef
      .backdropClick()
      .subscribe(() => this.componentRef?.instance.handleBackdropClick());

    this.componentRef.setInput('data', data);
    this.componentRef.setInput('boundary', boundary);
    this.componentRef.setInput('overlayRef', this.overlayRef);
    this.componentRef.setInput('minimized', options?.minimized ?? false);
    this.componentRef.setInput('restore', options?.restore ?? null);
    this.componentRef.instance.close.subscribe(() => this.close());
  }

  public updateContext(
    candidate: EvaluationPanelCandidate | null,
    availableOffers: EvaluationPanelOffer[],
    mentionUsers: EvaluationPanelMentionUsers | null = null,
  ): void {
    this.componentRef?.setInput('candidate', candidate);
    this.componentRef?.setInput('availableOffers', availableOffers);
    this.componentRef?.setInput('mentionUsers', mentionUsers);
  }

  public close(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.componentRef = null;
  }
}
